import { type RPPDomain } from '@retailer-platform/dashboard/core'
import { AdminNavEntryPointV2, NavEntryPoint } from '@retailer-platform/dashboard/routing'
import { onboardingAdminRouter } from './routing/onboardingAdminRouter'
import { onboardingRouter } from './routing/onboardingRouter'
import { onboardingStoreConfigScopedRouter } from './routing/onboardingStoreConfigScopedRouter.tsx'
import { onboardingPublicRouter } from './routing/onboardingPublicRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import {
  adminStorefrontSetupViewAccessControl,
  mobileManagementViewAccessControl,
  whitelabelSiteCustomizableStringsViewAccessControl,
} from './access-control/onboardingAccess.configuration.ts'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'onboarding',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'onboarding-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  lazyTranslations: {
    ES_ES: () => import('./translations/es-ES.json'),
    FR_FR: () => import('./translations/fr-FR.json'),
  },
  routers: {
    scoped: [onboardingStoreConfigScopedRouter, onboardingRouter],
    admin: [onboardingAdminRouter],
    public: [onboardingPublicRouter],
  },
  navigationEntries: [
    {
      attachToV2: NavEntryPoint.UserMenuSettings,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Set up and configure storefront site settings and appearance',
          labelId: 'onboardingDomain.siteSetup.navLabel',
          route: 'onboarding-site-setup',
          accessControl: adminStorefrontSetupViewAccessControl,
          id: 'onboarding-site-setup',
          positionNavV2: 7, // after the 'Storefront Onboarding' options
          subRoutes: ['onboarding-site-setup-review'],
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.Merchandising,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Customize and manage text strings displayed throughout the storefront',
          labelId: 'onboardingDomain.navItem.stringCustomization',
          route: 'onboarding-string-customizations',
          accessControl: whitelabelSiteCustomizableStringsViewAccessControl,
          isNew: true,
        },
      ],
    },
  ],
  adminNavigationEntries: [
    {
      attachToV2: AdminNavEntryPointV2.Operations,
      subNavItems: [
        {
          route: 'app-admin-onboarding-site-overview',
          labelId: 'onboardingDomain.siteOverview.navLabel',
          accessControl: adminStorefrontSetupViewAccessControl,
          positionNavV2: 10, // The nav entry point might need to be adjusted
        },
        {
          route: 'app-admin-onboarding-mobile-dashboard',
          labelId: 'onboardingDomain.mobileDashboard.navLabel',
          accessControl: mobileManagementViewAccessControl,
          positionNavV2: 11, // The nav entry point might need to be adjusted
        },
        {
          route: 'app-admin-onboarding-flip-dashboard',
          labelId: 'onboardingDomain.flipDashboard.navLabel',
          accessControl: adminStorefrontSetupViewAccessControl,
          positionNavV2: 12,
          subRoutes: [
            'app-admin-onboarding-flip-site-detail',
            'app-admin-onboarding-flip-feature-detail',
          ],
        },
      ],
    },
  ],
}

export default definition
